import { ActionReducerMapBuilder, createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { checkNameByType } from '../utils/helpers'
import {
    selectActiveDocumentMapping,
    selectDocumentMappingByActiveFloorId,
    selectDrawableActiveFloor,
} from './documents'
import { GeometricDrawable, selectAllDrawableGroupsGeometries } from './geometry'
import { selectProjectWithId } from './project'
import { ADD_BUILDING, DELETE_BUILDING, SET_PROJECT_BUILDINGS, UPDATE_BUILDING } from '../../actions/drawable'
import {
    BUILDING_SETTING_KEYS,
    BUILDING_SETTING_OPTION_KEYS,
    FIELD_KEY,
    otherValue,
    soffitTypeVentTypesV2,
} from '../../components/projects/project-settings/utils/constants'
import {
    transformBuildingFromApiToUi,
    transformBuildingsFromApiToUi,
} from '../../components/projects/project-settings/utils/helpers'
import { DocumentMapping } from '../../models/documentMapping'
import { Building } from '../../models/projectBuilding'
import { PLAN_VIEWS_ENUM } from '../../shared/constants/plan-views'
import { RootState } from '../../stores'

export const setBuildingsAction = createAction<{ buildings: Building[] }>(SET_PROJECT_BUILDINGS)
export const updateBuilding = createAction<{ building: Building }>(UPDATE_BUILDING)
export const deleteBuilding = createAction<{ buildingId: Building['id'] }>(DELETE_BUILDING)
export const addBuilding = createAction<Building>(ADD_BUILDING)

export type BuildingsState = {
    buildings: Building[]
    activeBuilding: Building | null
    buildingActiveTab: BUILDING_SETTING_KEYS | null
    buildingSettingOptions: any // TODO: update any
}

type UpdateKeyValueType = {
    [x: string]: string | number | boolean | null | object
}

export const initialBuildingsSate: BuildingsState = {
    buildings: [],
    activeBuilding: null,
    buildingActiveTab: null,
    buildingSettingOptions: {},
}

const handleSetBuildings = (state: BuildingsState, action: PayloadAction<{ buildings: Building[] }>) => {
    const buildings = action.payload && action.payload.buildings ? action.payload.buildings : []

    state.buildings = transformBuildingsFromApiToUi(buildings)
}

const handleUpdateBuilding = (state: BuildingsState, action: PayloadAction<{ building: Building }>) => {
    state.buildings = state.buildings.map((building) => {
        if (action.payload && action.payload.building?.id && building.id === action.payload.building.id) {
            return transformBuildingFromApiToUi(action.payload.building)
        }

        return building
    })
}

const handleDeleteBuilding = (state: BuildingsState, action: PayloadAction<{ buildingId: Building['id'] }>) => {
    state.buildings = state.buildings.filter((building) => building.id !== action.payload.buildingId)
}

const handleAddBuilding = (state: BuildingsState, action: PayloadAction<Building>) => {
    state.buildings = action.payload
        ? [...state.buildings, transformBuildingFromApiToUi(action.payload)]
        : state.buildings
}

const handleSetActiveBuilding = (state: BuildingsState, action: PayloadAction<{ building: Building }>) => {
    state.activeBuilding = state.buildings.find((building) => building.id === action.payload.building.id) || null
}

const handleSetBuildingActiveTab = (state: BuildingsState, action: PayloadAction<BUILDING_SETTING_KEYS | null>) => {
    state.buildingActiveTab = action.payload
}

const handleSetBuildingSettingOptions = (state: BuildingsState, action: PayloadAction<any>) => {
    const { tabKey, options } = action.payload

    state.buildingSettingOptions[tabKey] = options
}

const handleSetBuildingsSelectedOptions = (
    state: BuildingsState,
    action: PayloadAction<{
        key: string
        value: string | number | boolean | object | null
    }>
) => {
    const { key, value } = action.payload

    const updatedBuildings = [...state.buildings].map((building) => {
        if (building?.id === state.activeBuilding!.id) {
            if (state.buildingActiveTab === BUILDING_SETTING_KEYS.LUMBER_SETTINGS) {
                const [material, field, selection] = key.split('-')

                return {
                    ...building,
                    settings: {
                        ...building?.settings,
                        lumber_settings: {
                            ...building?.settings?.lumber_settings,
                            [material]: {
                                ...building?.settings?.lumber_settings?.[material],
                                [field]: {
                                    ...(building?.settings?.lumber_settings?.[material]?.[field] as object),
                                    [selection]: value,
                                },
                            },
                        },
                    },
                }
            }

            // waste_factors and project_materials are under framing_settings
            if (
                state.buildingActiveTab === BUILDING_SETTING_KEYS.WASTE_FACTORS ||
                state.buildingActiveTab === BUILDING_SETTING_KEYS.PROJECT_MATERIALS
            ) {
                const buildingData = {
                    ...building,
                    settings: {
                        ...building?.settings,
                        framing_settings: {
                            ...building?.settings.framing_settings,
                            [state.buildingActiveTab]: {
                                ...building?.settings.framing_settings[state.buildingActiveTab],
                                [key]: value,
                            },
                        },
                    },
                }

                // slab on grade and ground floor treated can't be selected at the same time
                // when slab on grade is selected, unset ground floor treated
                if (BUILDING_SETTING_OPTION_KEYS.SLAB_ON_GRADE === key && value) {
                    buildingData.settings.framing_settings[state.buildingActiveTab][
                        BUILDING_SETTING_OPTION_KEYS.GROUND_FLOOR_TREATED
                    ] = false
                }

                // when a ground floor treated is selected, unset slab on grade
                if (BUILDING_SETTING_OPTION_KEYS.GROUND_FLOOR_TREATED === key && value) {
                    buildingData.settings.framing_settings[state.buildingActiveTab][
                        BUILDING_SETTING_OPTION_KEYS.SLAB_ON_GRADE
                    ] = false
                }

                return buildingData
            }

            // reset rafter tail fields
            if (key === FIELD_KEY.RAFTER_TAIL) {
                if (value === 'NONE') {
                    building.settings.eaves_and_gables_settings.rafter_tail_eaves_dimensions_material = null
                    building.settings.eaves_and_gables_settings.rafter_tail_gables_dimensions_material = null
                    building.settings.eaves_and_gables_settings['rafter_tail_eaves_&_gables_dimensions_material'] = null
                    building.settings.eaves_and_gables_settings.rafter_tail_spacing = null
                    building.settings.eaves_and_gables_settings.replace_roof_sheathing = null
                }
                building.settings.eaves_and_gables_settings.exposed_soffit = value as string
            }

            // reset soffit vent type
            if (key === FIELD_KEY.SOFFIT_TYPE && !soffitTypeVentTypesV2.includes((value as string)?.toLowerCase())) {
                building.settings.eaves_and_gables_settings.soffit_vent_type = null
            }

            const preparedValue = typeof value === 'string' ? value.toLowerCase() : value

            if (key === FIELD_KEY.SOFFIT_TYPE) {
                if (preparedValue !== 'dimensional lumber') {
                    // reset conditional overhangs soffit object
                    if (building.settings.lumber_settings?.['overhangs']) {
                        building.settings.lumber_settings['overhangs'].soffit = null
                    }
                }

                if (preparedValue !== 'panel (4x8)') {
                    building.settings.eaves_and_gables_settings.panel_type = null
                    building.settings.eaves_and_gables_settings.texture = null
                    building.settings.eaves_and_gables_settings.profile = null

                    // reset conditional overhangs soffit_panel object
                    if (building.settings.lumber_settings?.['overhangs']) {
                        building.settings.lumber_settings['overhangs'].soffit_plywood = null
                    }
                }
            }

            if (key === FIELD_KEY.PANEL_TYPE) {
                if (!['fiber cement', 'lp'].includes(preparedValue as string)) {
                    building.settings.eaves_and_gables_settings.texture = null
                }

                if (!['plywood'].includes(preparedValue as string)) {
                    building.settings.eaves_and_gables_settings.profile = null

                    // reset conditional overhangs soffit_panel object
                    if (building.settings.lumber_settings?.['overhangs']) {
                        building.settings.lumber_settings['overhangs'].soffit_plywood = null
                    }
                }
            }
            if (FIELD_KEY.EXPOSED_SOFFIT === key) {
                building.settings.eaves_and_gables_settings.soffit_type = null
            }

            let updateKeyValue: UpdateKeyValueType = {
                [key]:
                    state.buildingActiveTab === BUILDING_SETTING_KEYS.EXTERIOR_WASTE_FACTORS &&
                    typeof value === 'string'
                        ? Number(value)
                        : value,
            }

            // used to unset the field_other values to placeholder instead of previous selected value
            const otherKey = `${key}_other`

            if (
                building?.settings[state.buildingActiveTab as BUILDING_SETTING_KEYS]?.hasOwnProperty(otherKey) &&
                value === otherValue
            ) {
                updateKeyValue = {
                    [key]: value,
                    [otherKey]: null,
                }
            }

            return {
                ...building,
                settings: {
                    ...building?.settings,
                    [state.buildingActiveTab as BUILDING_SETTING_KEYS]: {
                        ...building?.settings[state.buildingActiveTab as BUILDING_SETTING_KEYS],
                        ...updateKeyValue,
                    },
                },
            }
        }

        return building
    })

    const updatedActiveBuilding = updatedBuildings.find((building) => building.id === state.activeBuilding!.id)

    if (updatedActiveBuilding) {
        state.activeBuilding = updatedActiveBuilding as Building
    }

    state.buildings = updatedBuildings as Building[]
}

const extraReducers = (builder: ActionReducerMapBuilder<BuildingsState>): void => {
    builder.addCase(setBuildingsAction, handleSetBuildings)
    builder.addCase(updateBuilding, handleUpdateBuilding)
    builder.addCase(deleteBuilding, handleDeleteBuilding)
    builder.addCase(addBuilding, handleAddBuilding)
    builder.addCase(selectProjectWithId, () => {
        // buildingSettingOptions is keyed by tab name only, so options filtered for the previous
        // project's isMasterSet flag would leak into the next project. Reset the whole slice; the
        // new project's buildings arrive via setBuildingsAction right after.
        return { ...initialBuildingsSate, buildingSettingOptions: {} }
    })
}

const buildingsSlice = createSlice({
    name: 'buildings',
    initialState: initialBuildingsSate,
    reducers: {
        setBuildings: handleSetBuildings,
        setActiveBuilding: handleSetActiveBuilding,
        setBuildingActiveTab: handleSetBuildingActiveTab,
        setBuildingSettingOptions: handleSetBuildingSettingOptions,
        setBuildingsSelectedOptions: handleSetBuildingsSelectedOptions,
    },
    extraReducers,
})

export const {
    setBuildings,
    setActiveBuilding,
    setBuildingActiveTab,
    setBuildingSettingOptions,
    setBuildingsSelectedOptions,
} = buildingsSlice.actions

export const selectBuildings = createSelector(
    (state: RootState) => state.IMUP.buildings.buildings,
    (buildings) => buildings
)

export const selectActiveBuilding = createSelector(
    (state: RootState) => state.IMUP.buildings.activeBuilding,
    (activeBuilding) => activeBuilding
)

export const selectBuildingActiveTab = createSelector(
    (state: RootState) => state.IMUP.buildings.buildingActiveTab,
    (buildingActiveTab) => buildingActiveTab
)

export const selectBuildingSettingOptions = createSelector(
    (state: RootState) => state.IMUP.buildings.buildingSettingOptions,
    (buildingSettingOptions) => buildingSettingOptions
)

export const selectBuildingSettingFromActiveTab = createSelector(
    (state: RootState) => state.IMUP.buildings.buildingSettingOptions,
    (state: RootState) => state.IMUP.buildings.buildingActiveTab,
    (buildingSettingOptions, buildingActiveTab) => buildingSettingOptions[buildingActiveTab!]
)
export const selectActiveBuildingFromActiveFloor = createSelector(
    [(state: RootState) => selectDrawableActiveFloor(state), (state: RootState) => state.IMUP.buildings.buildings],
    (floor, buildings) => buildings.find((b: Building) => b.id === floor?.building_id)
)

export const selectActiveBuildingFromClassificationPage = createSelector(
    [(state: RootState) => selectActiveDocumentMapping(state), (state: RootState) => state.IMUP.buildings.buildings],
    (floor, buildings) => buildings.find((b: Building) => b.id === floor?.building_id)
)

export const selectActiveBuildingFromActiveFloorDocId = createSelector(
    [
        (state: RootState) => selectDocumentMappingByActiveFloorId(state),
        (state: RootState) => state.IMUP.buildings.buildings,
    ],
    (activeMapping, buildings) => buildings.find((b: Building) => b.id === activeMapping?.building_id)
)

export const selectMaterialAndPagesPerBuilding = createSelector(
    [
        (state: RootState) => state.IMUP.buildings.buildings,
        (state: RootState) => state.IMUP.documents.projectDocumentMappings,
        (state: RootState) => selectAllDrawableGroupsGeometries(state),
    ],
    (buildings, mappings, drawables) => {
        const geoDrawables = drawables.flatMap((group) => group.openings)

        return buildings.reduce((perBuildingRecord, building) => {
            const buildingMappings = mappings ? mappings.filter((dm) => dm.building_id === building.id) : []
            const buildingMappingDocChunkId = buildingMappings.map((dm) => dm.document_chunk_id)
            const buildingDrawables = geoDrawables.filter((geoDrawable) =>
                buildingMappingDocChunkId.includes(geoDrawable.opening_locations[0].document_chunk_id)
            )

            perBuildingRecord.push({
                building,
                material: buildingDrawables,
                pages: buildingMappings,
            })

            return perBuildingRecord
        }, [] as { building: Building; pages: DocumentMapping[]; material: GeometricDrawable[] }[])
    }
)

export const selectClassifiedFloorLevelsForBuilding = createSelector(
    [
        (state: RootState) => state.IMUP.buildings.activeBuilding,
        (state: RootState) => state.IMUP.documents.projectDocumentMappings,
        (state: RootState) => state.IMUP.documents.documentChunks,
        (state: RootState) => state.IMUP.region.regions,
    ],
    (activeBuilding, mappings, chunks, regions) => {
        if (!activeBuilding || !mappings) return []

        const deletedChunkIds = (chunks ?? []).reduce<Set<number>>((acc, c) => {
            if (c.is_user_deleted) acc.add(c.id)

            return acc
        }, new Set())

        const buildingFloorMappings = mappings.filter(
            (m) =>
                m.building_id === activeBuilding.id &&
                m.type === PLAN_VIEWS_ENUM.FLOOR &&
                !deletedChunkIds.has(m.document_chunk_id)
        )

        const buildingChunkIds = new Set(chunks?.filter((c) => c.buildingID === activeBuilding.id).map((m) => m.id))
        const floorNames: string[] = []

        // Collect recognized floor plan names from both page_name and any regions inside the page.
        // Some plans split multiple floor levels onto one page via regions, so each region name may
        // contribute its own wall_sheathing_size field. Names are deduped so shared page/region names
        // produce a single field.
        const addIfFloor = (name: string | undefined | null) => {
            if (name && checkNameByType(PLAN_VIEWS_ENUM.FLOOR, name) && !floorNames.includes(name)) {
                floorNames.push(name)
            }
        }

        buildingFloorMappings.forEach((mapping) => addIfFloor(mapping.page_name))
        ;(regions ?? []).forEach((region) => {
            if (buildingChunkIds.has(region.document_chunk_id)) addIfFloor(region.name)
        })

        return floorNames
    }
)

export default buildingsSlice
